import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { stockImages } from "@/data/site/stockImages";
import { PageSEO } from "@/components/site/PageSEO";
import { CTASection } from "@/components/site/CTASection";
import { brand, founder } from "@/data/site/brand";
import { Reveal, RevealStagger, RevealItem } from "@/components/motion/Reveal";

const About = () => (
  <SiteLayout>
    <PageSEO
      title="About Comprehensive Tax Resolution LLC"
      description="A CPA firm built around one job: resolving IRS problems. Led by Franklin Sofi, MBA, CPA, CTRS — 15+ years of representation, ASTPS Gold-tier credentialing."
      path="/about"
      image={stockImages.consultation.src}
    />

    {/* 1. Hero */}
    <section className="relative bg-gradient-navy text-white overflow-hidden">
      <HeroBackground image={stockImages.consultation} priority />
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">Our firm</p>
          <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 [text-wrap:balance]">
            Most "tax relief" companies sell.<br />
            <span className="text-gold">We're a CPA firm. We resolve.</span>
          </h1>
          <p className="text-lg text-white/80 max-w-2xl leading-relaxed">
            {brand.legalName} is a credentialed Texas CPA firm representing individuals and
            businesses nationwide in IRS collections, audits, and tax debt resolution — with a
            single, narrow focus and no commissioned sales team.
          </p>
        </Reveal>
      </div>
    </section>

    {/* 2. Origin story — editorial */}
    <section className="py-16 lg:py-32 bg-white">
      <div className="container mx-auto px-4 max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">
          Why this firm exists
        </p>
        <h2 className="font-heading text-3xl lg:text-5xl font-bold text-navy leading-[1.15] mb-10 [text-wrap:balance]">
          Built after watching too many taxpayers lose money to the wrong help.
        </h2>

        <div className="space-y-6 text-[17px] leading-[1.75] text-foreground/85">
          <p>
            Before founding Comprehensive Tax Resolution, {founder.name} spent years inside the tax
            industry watching the same pattern repeat: a scared taxpayer gets a letter from the
            IRS, calls a 1-800 number from a late-night ad, and ends up paying $5,000–$10,000
            upfront to a "case manager" who isn't even licensed.
          </p>
          <p>
            Six months later the IRS notice is still on the kitchen counter, the retainer is gone,
            and the only thing that's changed is the penalty balance — which has grown.
          </p>
          <p>
            That's the gap this firm was built to close. A licensed CPA, credentialed in tax
            resolution, with flat-fee engagements and direct access — for the same money those
            "relief" mills charge for a sales pitch.
          </p>
        </div>

        <blockquote className="border-l-2 border-gold pl-6 my-12 text-navy italic text-lg lg:text-xl leading-relaxed">
          "I won't take a case I can't win. If someone calls and they're better off filing on
          their own or going straight to the IRS payment portal, I tell them that. It costs me a
          client and earns me a referral every single time."
          <footer className="text-sm not-italic text-foreground/60 mt-4">
            — {founder.name}, {founder.credentials}
          </footer>
        </blockquote>
      </div>
    </section>

    {/* 3. Who we are + difference — secondary */}
    <section className="py-16 lg:py-32 bg-secondary">
      <div className="container mx-auto px-4 max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">Who we are</p>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy leading-[1.15] mb-6 [text-wrap:balance]">
          A CPA firm — not a relief mill, not a marketing operation.
        </h2>
        <p className="text-[17px] leading-[1.75] text-foreground/85 mb-12">
          A CPA is a federally authorized tax practitioner — the same authority as a tax attorney
          or enrolled agent in front of the IRS, with the added advantage of deep
          financial-statement and accounting expertise. That matters because almost every IRS
          resolution program turns on financial documentation. Get the numbers right and the
          program approves; get them wrong and you're rejected for years.
        </p>

        <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">
          What makes us different
        </p>
        <RevealStagger className="space-y-3.5" stagger={70}>
          {[
            "Credentialed CPA on every case — never assigned to a non-licensed 'case manager.'",
            "Flat-fee engagements with clear scope. No surprise add-ons, no hourly creep.",
            "15+ years of IRS collections, audit, and Offer in Compromise experience.",
            "ASTPS Gold Member — the highest tier of tax-resolution credentialing in the U.S.",
            "Direct phone access to your specialist throughout the engagement.",
            "We turn away cases we can't win. Most firms won't.",
          ].map((p) => (
            <RevealItem key={p}>
              <div className="flex items-start gap-3 text-foreground/85">
                <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0 mt-1" aria-hidden="true" />
                <span>{p}</span>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
      </div>
    </section>

    {/* 4. Process — white */}
    <section className="py-16 lg:py-32 bg-white">
      <div className="container mx-auto px-4 max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">How we work</p>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy leading-[1.15] mb-10 [text-wrap:balance]">
          Our process, in four steps.
        </h2>
        <RevealStagger className="space-y-8" stagger={90}>
          {[
            ["Free case review", "We listen, pull your IRS transcripts (with your authorization), and explain exactly what you're facing — in plain English."],
            ["Investigation", "We confirm your balances, filing status, statute of limitations, and any active enforcement (levies, liens, garnishments)."],
            ["Resolution plan", "Based on your financials, we identify which IRS program you actually qualify for — and which ones the 'tax relief' ads were lying about."],
            ["Negotiation & filing", "We prepare and file every form, handle every IRS call, and push your case to the best possible outcome."],
          ].map(([title, body], i) => (
            <RevealItem key={title}>
              <div className="flex gap-6">
                <div className="font-heading text-3xl font-bold text-gold leading-none w-10 flex-shrink-0" aria-hidden="true">
                  0{i + 1}
                </div>
                <div>
                  <h3 className="font-heading font-bold text-navy text-xl mb-2">{title}</h3>
                  <p className="text-foreground/75 leading-relaxed">{body}</p>
                </div>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>

        <div className="mt-16 pt-10 border-t border-border">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-3">
            Meet the founder
          </p>
          <h3 className="font-heading text-2xl font-bold text-navy mb-2">
            {founder.name}, <span className="text-foreground/60 font-normal">{founder.credentials}</span>
          </h3>
          <p className="text-foreground/70 mb-4">{founder.title}</p>
          <Link
            to="/about/franklin-sofi"
            className="inline-flex items-center gap-1 font-semibold text-navy hover:text-gold"
          >
            Read Franklin's full bio <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>

    <CTASection />
  </SiteLayout>
);

export default About;
